import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { SbNotification } from "uiCore";
import usePAICStore from "Store/usePAICStore";
import useAuthStore from "Store/useAuthStore";
import { PAIC_ROUTES } from "constants/paicRoutes.constants";
import { PAIC_VIEW_MODES, handlePAICErrorCode, PAIC_ERROR_MESSAGES } from "constants/peoplePlusAI.constants";

import { getPAICJDs } from "Api/apiService";
import analytics from "Utils/analytics";
import sanitizeMarkdown from "Utils/sanitizeMarkdown";

const formatLastUpdatedText = (timestamp) => {
  if (!timestamp) return "";

  const rawDate =
    timestamp?.$date ||
    timestamp?.date ||
    timestamp;

  const parsedDate = new Date(rawDate);

  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  const dateStr = parsedDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const timeStr = parsedDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return `${dateStr}, ${timeStr}`;
};

const formatVersion = (value) => {
  if (value === null || value === undefined || value === "") {
    return "1.0";
  }

  const num = Number(value);
  return Number.isNaN(num) ? String(value) : num.toFixed(1);
};

const usePACPeopleJDViewV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const routeSelectionId = location.state?.selectionId;
  const selectionId = usePAICStore((state) => state.selectionId);
  const email = useAuthStore((state) => state.email);

  const projectId = routeSelectionId || selectionId;

  const [loading, setLoading] = useState(false);
  const [paicJds, setPaicJds] = useState([]);
  const [selectedJdId, setSelectedJdId] = useState(null);
  const [viewMode, setViewMode] = useState(PAIC_VIEW_MODES.VIEW);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!projectId) return;

      setLoading(true);

      analytics.trackPAICPeopleJDViewOpened?.({
        from: "pac_people_jd_view",
        email,
        description: "Opened People JD view",
      });

      try {
        const response = await getPAICJDs(projectId);
        if (!isMounted) return;

        const safeData = Array.isArray(response) ? response : [];
        setPaicJds(safeData);

        if (safeData.length > 0) {
          setSelectedJdId(safeData[0].sb_paic_jd_unique_id);
        } else {
          setSelectedJdId(null);
        }
      } catch (err) {
        if (!handlePAICErrorCode(err, SbNotification.error)) {
          SbNotification.error(PAIC_ERROR_MESSAGES.LOAD_PEOPLE_JD_FAILED);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [projectId, email]);

  const jdOptions = useMemo(() => {
    return paicJds.map((jd) => ({
      value: jd.sb_paic_jd_unique_id,
      label:
        jd.job_title ||
        jd.paic_jd?.human_jd?.title ||
        "Untitled JD",
    }));
  }, [paicJds]);

  const selectedJdData = useMemo(() => {
    return paicJds.find((jd) => jd.sb_paic_jd_unique_id === selectedJdId) || null;
  }, [paicJds, selectedJdId]);

const markdownData = useMemo(() => {
  if (!selectedJdData) return "";

  // Backend provides markdown - try common field names
  const raw =
    selectedJdData.agent_jd_md ||
    selectedJdData.paic_jd_md ||
    selectedJdData.human_jd_md ||
    selectedJdData.jd_markdown ||
    selectedJdData.markdown ||
    selectedJdData.paic_jd?.markdown ||
    "";

  return sanitizeMarkdown(raw);
}, [selectedJdData]);

  const versionOptions = useMemo(() => {
    const uniqueVersions = [
      ...new Map(
        paicJds.map((jd) => {
          const version = formatVersion(jd.version);
          return [
            version,
            {
              label: `JD Version - ${version}`,
              value: version,
            },
          ];
        }),
      ).values(),
    ];

    return uniqueVersions.length > 0
      ? uniqueVersions
      : [{ label: "JD Version - 1.0", value: "1.0" }];
  }, [paicJds]);

  const selectedVersion = useMemo(() => {
    return formatVersion(selectedJdData?.version);
  }, [selectedJdData]);

  const modifiedText = useMemo(() => {
    const formattedDate = formatLastUpdatedText(selectedJdData?.last_updated_time_stamp);

    return formattedDate ? `Modified on ${formattedDate}` : "";
  }, [selectedJdData]);

  const ownerText = useMemo(() => {
    const ownerName =
      selectedJdData?.current_owner_full_name ||
      selectedJdData?.owner_full_name ||
      selectedJdData?.ownerName ||
      "";

    return ownerName ? `Owner: ${ownerName}` : "";
  }, [selectedJdData]);

  const documentStatus = useMemo(() => {
    return (
      selectedJdData?.agent_jd_status ||
      selectedJdData?.paic_jd_status ||
      selectedJdData?.status ||
      ""
    );
  }, [selectedJdData]);

  const handleVersionChange = useCallback(
    (versionValue) => {
      const matchedItem = paicJds.find(
        (jd) => formatVersion(jd.version) === String(versionValue),
      );

      if (matchedItem?.sb_paic_jd_unique_id) {
        setSelectedJdId(matchedItem.sb_paic_jd_unique_id);
      }
    },
    [paicJds],
  );

  const handlePagination = useCallback(
    (direction) => {
      if (!jdOptions.length) return;

      let index = jdOptions.findIndex((item) => item.value === selectedJdId);

      if (direction === "PREV") {
        index = index <= 0 ? jdOptions.length - 1 : index - 1;
      } else {
        index = index >= jdOptions.length - 1 ? 0 : index + 1;
      }

      const nextId = jdOptions[index]?.value;
      if (nextId) {
        setSelectedJdId(nextId);
      }

      analytics.trackPAICPeopleJDPagination?.({
        from: "pac_people_jd_view",
        email,
        direction,
        description: `People JD pagination: ${direction}`,
      });
    },
    [jdOptions, selectedJdId, email],
  );

  const handleClose = useCallback(() => {
    analytics.trackPAICCloseDocuments?.({
      from: "pac_people_jd_view",
      email,
      description: "Closed People JD view",
    });

    navigate(PAIC_ROUTES.GROUP_AGENTS);
  }, [email, navigate]);

  const enterEditMode = useCallback(() => {
    setViewMode(PAIC_VIEW_MODES.EDIT);

    analytics.trackPAICPeopleJDEditMode?.({
      from: "pac_people_jd_view",
      email,
      description: "Entered edit mode for People JD",
    });
  }, [email]);

  const cancelEditMode = useCallback(() => {
    setViewMode(PAIC_VIEW_MODES.VIEW);

    analytics.trackPAICPeopleJDCancel?.({
      from: "pac_people_jd_view",
      email,
      description: "Cancelled People JD edit",
    });
  }, [email]);

  // const handleRevise = useCallback(() => {}, []); // commented out for now

  const handleDownloadTrack = useCallback(() => {
    analytics.trackDownloadJD?.({
      from: "pac_people_jd_view",
      email,
      description: "Downloaded People JD",
    });
  }, [email]);

  // const handlePublishToggle = useCallback(() => {
  //   console.log("Publish / Unpublish clicked");
  // }, []);

  return {
    loading,
    viewMode,
    projectId,
    paicJds,
    jdOptions,
    versionOptions,
    selectedJdId,
    selectedVersion,
    selectedJdData,
    markdownData,
    modifiedText,
    ownerText,
    documentStatus,
    setSelectedJdId,
    handleVersionChange,
    handlePagination,
    handleClose,
    enterEditMode,
    cancelEditMode,
    // handleRevise,        // commented out for now
    handleDownloadTrack,
    // handlePublishToggle, // commented out for now
  };
};

export default usePACPeopleJDViewV2;